import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
} from "react";
import {
  ThemeProvider as MuiThemeProvider,
  createTheme,
} from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { useOrganization } from "./OrganizationContext";
import { createPadelEagleTheme } from "../theme/padelEagleTheme";

type ThemeMode = "light" | "dark";

interface ThemeContextType {
  mode: ThemeMode;
  toggleTheme: () => void;
  orgPrimaryColor: string;
  orgSecondaryColor: string;
  orgLogo: string | undefined;
}

const ThemeContext = createContext<ThemeContextType>({
  mode: "light",
  toggleTheme: () => {},
  orgPrimaryColor: "#1976d2",
  orgSecondaryColor: "#dc004e",
  orgLogo: undefined,
});

export const useTheme = () => useContext(ThemeContext);

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { currentOrg, loading: orgLoading } = useOrganization();
  const [mode, setMode] = useState<ThemeMode>(() => {
    const savedMode = localStorage.getItem("themeMode");
    return savedMode === "dark" || savedMode === "light" ? savedMode : "light";
  });

  // Extract organization colors with defaults
  const orgPrimaryColor = currentOrg?.branding?.primaryColor || "#1976d2";
  const orgSecondaryColor = currentOrg?.branding?.secondaryColor || "#dc004e";
  const orgAccentColor = currentOrg?.branding?.accentColor || "#4caf50";
  const orgTextColor = currentOrg?.branding?.textColor;
  const orgLogo = currentOrg?.branding?.logo;

  useEffect(() => {
    localStorage.setItem("themeMode", mode);
  }, [mode]);

  // Apply organization branding to CSS variables
  useEffect(() => {
    if (currentOrg && !orgLoading) {
      const root = document.documentElement;
      
      // Apply CSS custom properties for organization branding
      root.style.setProperty('--org-primary', orgPrimaryColor);
      root.style.setProperty('--org-secondary', orgSecondaryColor);
      root.style.setProperty('--org-accent', orgAccentColor);
      root.style.setProperty('--org-font-family', currentOrg.branding?.fontFamily || 'Inter, sans-serif');
      root.style.setProperty('--org-heading-font', currentOrg.branding?.headingFont || 'Poppins, sans-serif');
      root.style.setProperty('--org-border-radius', currentOrg.branding?.borderRadius || '0.5rem');
      
      // Spacing multiplier
      const spacingMap = { compact: '0.75', normal: '1', relaxed: '1.25' };
      const spacing = currentOrg.branding?.spacing || 'normal';
      root.style.setProperty('--org-spacing-multiplier', spacingMap[spacing]);

      // Update page title and favicon
      document.title = currentOrg.name;
      
      const faviconLink = document.querySelector("link[rel~='icon']") as HTMLLinkElement | null;
      if (faviconLink) {
        if (currentOrg.branding?.favicon) {
          faviconLink.href = currentOrg.branding.favicon;
        } else if (currentOrg.slug === "padel-eagle") {
          faviconLink.href = "/icon-padel-eagle.png";
        }
      }

      // Inject custom CSS if provided (Enterprise feature)
      if (currentOrg.branding?.customCSS) {
        let customStyleTag = document.getElementById('org-custom-css') as HTMLStyleElement;
        if (!customStyleTag) {
          customStyleTag = document.createElement('style');
          customStyleTag.id = 'org-custom-css';
          document.head.appendChild(customStyleTag);
        }
        customStyleTag.innerHTML = currentOrg.branding.customCSS;
      }
    }
  }, [currentOrg, orgLoading, orgPrimaryColor, orgSecondaryColor, orgAccentColor]);

  const toggleTheme = () => {
    setMode((prevMode) => (prevMode === "light" ? "dark" : "light"));
  };

  // Padel Eagle follows its CI styleguide (light only), independent of branding stored in the DB
  const isPadelEagle = currentOrg?.slug === "padel-eagle";

  const theme = useMemo(
    () =>
      isPadelEagle ? createPadelEagleTheme() : createTheme({
        palette: {
          mode,
          ...(mode === "light"
            ? {
                // Light mode colors - use organization colors
                primary: {
                  main: orgPrimaryColor,
                },
                secondary: {
                  main: orgSecondaryColor,
                },
                success: {
                  main: orgAccentColor,
                  light: "#e8f5e8",
                  dark: "#388e3c",
                },
                error: {
                  main: "#f44336",
                  light: "#ffebee",
                  dark: "#d32f2f",
                },
                warning: {
                  main: "#ff9800",
                  light: "#fff3e0",
                  dark: "#f57c00",
                },
                background: {
                  default: currentOrg?.branding?.backgroundColor || "#f5f5f5",
                  paper: "#ffffff",
                },
                ...(orgTextColor && {
                  text: {
                    primary: orgTextColor,
                  },
                }),
              }
            : {
                // Dark mode colors - adjusted organization colors
                primary: {
                  main: adjustColorForDarkMode(orgPrimaryColor),
                },
                secondary: {
                  main: adjustColorForDarkMode(orgSecondaryColor),
                },
                success: {
                  main: "#66bb6a",
                  light: "#1b3a1e",
                  dark: "#388e3c",
                },
                error: {
                  main: "#f44336",
                  light: "#3d1a1a",
                  dark: "#d32f2f",
                },
                warning: {
                  main: "#ffa726",
                  light: "#3d2e1a",
                  dark: "#f57c00",
                },
                info: {
                  main: "#29b6f6",
                  light: "#1a2d3d",
                  dark: "#0288d1",
                },
                background: {
                  default: "#1a1a1a",
                  paper: "#2d2d2d",
                },
              }),
        },
        typography: {
          fontFamily: currentOrg?.branding?.fontFamily || 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          h1: {
            fontFamily: currentOrg?.branding?.headingFont || 'Poppins, sans-serif',
          },
          h2: {
            fontFamily: currentOrg?.branding?.headingFont || 'Poppins, sans-serif',
          },
          h3: {
            fontFamily: currentOrg?.branding?.headingFont || 'Poppins, sans-serif',
          },
          h4: {
            fontFamily: currentOrg?.branding?.headingFont || 'Poppins, sans-serif',
          },
          h5: {
            fontFamily: currentOrg?.branding?.headingFont || 'Poppins, sans-serif',
          },
          h6: {
            fontFamily: currentOrg?.branding?.headingFont || 'Poppins, sans-serif',
          },
        },
        shape: {
          borderRadius: parseFloat(currentOrg?.branding?.borderRadius || '8') || 8,
        },
        components: {
          MuiPaper: {
            styleOverrides: {
              root: {
                backgroundImage: "none",
              },
            },
          },
          MuiButton: {
            styleOverrides: {
              root: {
                borderRadius: currentOrg?.branding?.buttonStyle === 'pill' 
                  ? '999px' 
                  : currentOrg?.branding?.buttonStyle === 'sharp'
                  ? '0px'
                  : currentOrg?.branding?.borderRadius || '8px',
              },
            },
          },
        },
      }),
    [mode, orgPrimaryColor, orgSecondaryColor, orgAccentColor, orgTextColor, currentOrg, isPadelEagle]
  );

  return (
    <ThemeContext.Provider value={{ mode: isPadelEagle ? "light" : mode, toggleTheme, orgPrimaryColor: isPadelEagle ? "#743444" : orgPrimaryColor, orgSecondaryColor: isPadelEagle ? "#E49421" : orgSecondaryColor, orgLogo }}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

// Helper function to adjust colors for dark mode
function adjustColorForDarkMode(color: string): string {
  // Lighten the color for dark mode visibility
  // This is a simple implementation - you could use a color library for better results
  if (!color || !color.startsWith('#')) return color;
  
  const hex = color.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  
  // Lighten by adding 50 to each channel (capped at 255)
  const newR = Math.min(255, r + 50);
  const newG = Math.min(255, g + 50);
  const newB = Math.min(255, b + 50);
  
  return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
}
